import React, { useState } from 'react';
import { createUseStyles } from 'react-jss';
import { gql, useMutation, useQuery } from '@apollo/client';
import { T } from './tokens';

export const WIDGET_PASTES = gql`
  query WidgetPastes($target: String!) {
    widgetPastes(target: $target) {
      _id
      raw
      createdAt
      createdBy
    }
  }
`;

const SAVE_WIDGET_PASTE = gql`
  mutation SaveWidgetPaste($target: String!, $raw: String!) {
    saveWidgetPaste(target: $target, raw: $raw)
  }
`;

type Paste = { _id: string; raw: string; createdAt: string; createdBy: string | null };

/** Saves a kept paste to the widget's history. Failures don't block keeping. */
export const useSavePaste = (target: string): ((raw: string) => void) => {
  const [save] = useMutation(SAVE_WIDGET_PASTE, { refetchQueries: [{ query: WIDGET_PASTES, variables: { target } }] });
  return (raw: string) => {
    if (!raw.trim()) return;
    save({ variables: { target, raw } }).catch((e: Error) => console.warn('Couldn’t save paste history', e.message));
  };
};

const useStyles = createUseStyles({
  root: { fontSize: 13, color: T.fg },
  toggle: { all: 'unset', cursor: 'pointer', color: T.muted, fontSize: 12, fontWeight: 600 },
  list: { listStyle: 'none', margin: '8px 0 0', padding: 0, display: 'grid', gap: 6 },
  item: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gap: 10,
    alignItems: 'center',
    padding: '8px 10px',
    border: `1px solid ${T.line}`,
    borderRadius: 8,
    background: T.chrome
  },
  meta: { fontSize: 12, color: T.muted },
  snippet: { fontFamily: T.mono, fontSize: 11, color: T.fg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  load: {
    padding: '5px 12px',
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 980,
    border: `1px solid ${T.fieldLine}`,
    background: T.field,
    color: T.fg,
    cursor: 'pointer'
  }
});

// A readable first line of the code, without fences or the reply's prose.
const snippetOf = (raw: string): string => {
  const code = raw.includes('```') ? raw.split('```').filter((_, i) => i % 2 === 1).join('\n') : raw;
  const line = code.split('\n').map(l => l.trim()).find(l => l && !/^(html|css|js|javascript)$/i.test(l)) || '';
  return line.slice(0, 120);
};

const when = (iso: string) => {
  try { return new Date(iso).toLocaleString(); } catch (e) { return iso; }
};

type Props = {
  target: string;
  /** Loads a past paste as a new proposal. */
  onLoad: (raw: string) => void;
};

const PasteHistory: React.FC<Props> = ({ target, onLoad }) => {
  const c = useStyles();
  const [open, setOpen] = useState(false);
  const { data } = useQuery<{ widgetPastes: Paste[] }>(WIDGET_PASTES, { variables: { target }, skip: !target, fetchPolicy: 'cache-and-network' });
  const pastes = (data && data.widgetPastes) || [];
  if (!pastes.length) return null;
  return (
    <div className={c.root} data-testid="paste-history">
      <button type="button" className={c.toggle} aria-expanded={open} onClick={() => setOpen(x => !x)}>
        {open ? '▾' : '▸'} Earlier versions, pasted or generated ({pastes.length})
      </button>
      {open && (
        <ul className={c.list}>
          {pastes.map(p => (
            <li key={p._id} className={c.item}>
              <div style={{ minWidth: 0 }}>
                <div className={c.meta}>{when(p.createdAt)}{p.createdBy ? ` · ${p.createdBy}` : ''} · {Math.max(1, Math.round(p.raw.length / 1024))} KB</div>
                <div className={c.snippet}>{snippetOf(p.raw)}</div>
              </div>
              <button type="button" className={c.load} onClick={() => onLoad(p.raw)}>Load</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PasteHistory;
